<template>
  <div class="student-list">
    <header class="page-header">
      <div>
        <h2>潜在学员清单</h2>
        <p class="subtitle">查看已通过报名或 OCR 创建的潜在学员；结课后晋升为正式档案的也会显示「已晋升」</p>
      </div>
    </header>

    <section class="filter-panel">
      <div class="filter-item">
        <label>关键字</label>
        <input v-model="filters.keyword" placeholder="姓名 / 手机号 / 身份证" />
      </div>
      <div class="filter-actions">
        <button class="primary-button" @click="loadList">查询</button>
        <button class="ghost-button" @click="resetFilters">重置</button>
      </div>
    </section>

    <section class="table-wrapper" v-if="rows.length">
      <table>
        <thead>
          <tr>
            <th>姓名</th>
            <th>系统用户</th>
            <th>手机号</th>
            <th>身份证</th>
            <th>状态</th>
            <th>晋升正式档案</th>
            <th>建档时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td>{{ row.name || '—' }}</td>
            <td>{{ row.username ? row.username + (row.userId ? ' #' + row.userId : '') : '—' }}</td>
            <td>
              {{ getDisplay(row.id, 'phone', row.phone) }}
              <button
                type="button"
                class="decrypt-btn"
                @click="toggleDecrypt(row.id, 'phone')"
                :title="decryptedValues[keyOf(row.id, 'phone')] ? '隐藏' : '查看'"
              >
                <svg v-if="decryptedValues[keyOf(row.id, 'phone')]" class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <svg v-else class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              </button>
            </td>
            <td>
              {{ getDisplay(row.id, 'idNumber', row.idNumber) || '—' }}
              <button
                v-if="row.idNumber"
                type="button"
                class="decrypt-btn"
                @click="toggleDecrypt(row.id, 'idNumber')"
                :title="decryptedValues[keyOf(row.id, 'idNumber')] ? '隐藏' : '查看'"
              >
                <svg v-if="decryptedValues[keyOf(row.id, 'idNumber')]" class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                <svg v-else class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              </button>
            </td>
            <td>{{ statusLabel(row.status) }}</td>
            <td>{{ row.promotedFormalProfileId ? '#' + row.promotedFormalProfileId : '—' }}</td>
            <td>{{ formatDate(row.createdAt) }}</td>
            <td>
              <button class="link-button" @click="openDetail(row.id)">详情</button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="pagination">
        <button @click="changePage(pagination.page - 1)" :disabled="pagination.page === 0 || loading">上一页</button>
        <span>第 {{ pagination.page + 1 }} / {{ totalPages }} 页</span>
        <button @click="changePage(pagination.page + 1)" :disabled="pagination.page + 1 >= totalPages || loading">
          下一页
        </button>
      </div>
    </section>

    <div v-else class="empty-state">
      <p>暂无潜在学员档案</p>
    </div>

    <div v-if="detail" class="drawer-root">
      <div class="drawer-scrim" @click="closeDetail" aria-hidden="true"></div>
      <aside class="drawer" role="dialog" aria-modal="true" @click.stop>
        <div class="drawer-header">
          <h3>潜在学员详情 #{{ detail.id }}</h3>
          <button type="button" class="close-button" @click="closeDetail">×</button>
        </div>
        <div class="drawer-body">
        <section>
          <h4>基本信息</h4>
          <p><strong>姓名：</strong>{{ detail.name }}</p>
          <p><strong>现用名：</strong>{{ detail.currentName || '—' }}</p>
          <p><strong>状态：</strong>{{ statusLabel(detail.status) }}</p>
          <p>
            <strong>手机号：</strong>{{ getDisplay(detail.id, 'phone', detail.phone) }}
            <button
              type="button"
              class="decrypt-btn"
              @click="toggleDecrypt(detail.id, 'phone')"
              :title="decryptedValues[keyOf(detail.id, 'phone')] ? '隐藏' : '查看'"
            >
              <svg v-if="decryptedValues[keyOf(detail.id, 'phone')]" class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            </button>
          </p>
          <p v-if="detail.idNumber">
            <strong>身份证：</strong>{{ getDisplay(detail.id, 'idNumber', detail.idNumber) }}
            <button
              type="button"
              class="decrypt-btn"
              @click="toggleDecrypt(detail.id, 'idNumber')"
              :title="decryptedValues[keyOf(detail.id, 'idNumber')] ? '隐藏' : '查看'"
            >
              <svg v-if="decryptedValues[keyOf(detail.id, 'idNumber')]" class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else class="eye-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            </button>
          </p>
          <p><strong>性别：</strong>{{ detail.sex || '—' }}</p>
          <p><strong>民族：</strong>{{ detail.ethnicity || '—' }}</p>
          <p><strong>出生：</strong>{{ detail.birthDate || '—' }}</p>
          <p><strong>推荐码：</strong>{{ detail.referralCode || '—' }}</p>
          <p><strong>晋升正式档案 ID：</strong>{{ detail.promotedFormalProfileId || '—' }}</p>
          <p><strong>创建时间：</strong>{{ formatDate(detail.createdAt) }}</p>
        </section>
        <section>
          <h4>微信账号</h4>
          <div class="wechat-info">
            <p><strong>微信昵称：</strong>{{ detail.wechatNickname || '—' }}</p>
            <p><strong>openid：</strong>{{ detail.wechatOpenid || '—' }}</p>
            <button
              v-if="detail.wechatOpenid"
              type="button"
              class="danger-button"
              :disabled="loading"
              @click="unbindPotentialWechat"
            >
              解绑微信账号
            </button>
          </div>
        </section>
        <section>
          <h4>联系与其它</h4>
          <p><strong>QQ：</strong>{{ detail.qqNumber || '—' }}</p>
          <p><strong>微信：</strong>{{ detail.wechatNumber || '—' }}</p>
          <p><strong>住址：</strong>{{ detail.contactAddress || '—' }}</p>
        </section>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { studentService } from '@/services/studentService'
import { sensitiveDataService } from '@/services/sensitiveDataService'

const ENTITY = 'POTENTIAL_STUDENT'

export default {
  name: 'PotentialStudentList',
  data() {
    return {
      rows: [],
      loading: false,
      filters: { keyword: '' },
      pagination: { page: 0, size: 10, total: 0 },
      detail: null,
      decryptedValues: {}
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    keyOf(id, field) {
      return `${ENTITY}_${id}_${field}`
    },
    formatDate(value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').replace('Z', '')
    },
    statusLabel(s) {
      if (s === 'ACTIVE') return '在读（潜在）'
      if (s === 'PROMOTED') return '已晋升为正式档案'
      return s || '—'
    },
    async loadList() {
      try {
        this.loading = true
        const res = await studentService.fetchPotentialStudents({
          page: this.pagination.page,
          size: this.pagination.size,
          keyword: this.filters.keyword || undefined
        })
        if (res.success) {
          this.rows = res.data.content || []
          this.pagination.total = res.data.totalElements || 0
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters.keyword = ''
      this.pagination.page = 0
      this.loadList()
    },
    changePage(p) {
      if (p < 0 || p >= this.totalPages) return
      this.pagination.page = p
      this.loadList()
    },
    getDisplay(id, field, masked) {
      return this.decryptedValues[this.keyOf(id, field)] || masked
    },
    async toggleDecrypt(id, field) {
      const key = this.keyOf(id, field)
      if (this.decryptedValues[key]) {
        delete this.decryptedValues[key]
        return
      }
      try {
        const plain = await sensitiveDataService.decryptField(ENTITY, id, field)
        if (plain != null && plain !== '') {
          this.decryptedValues[key] = plain
        }
      } catch (e) {
        console.error(e)
        alert(e?.message || '解密失败')
      }
    },
    closeDetail() {
      this.detail = null
    },
    async openDetail(id) {
      try {
        const res = await studentService.fetchPotentialStudentDetail(id)
        if (res.success) {
          this.detail = res.data
        }
      } catch (e) {
        console.error(e)
        alert(e?.error || e?.message || '加载详情失败')
      }
    },
    async unbindPotentialWechat() {
      if (!this.detail?.id) return
      const confirmed = window.confirm(
        '确定要解绑该潜在学员的微信账号吗？解绑后与学员清单解绑行为一致：需使用新微信号重新登录并完成报名或身份证绑定，档案会以新手机号为准。'
      )
      if (!confirmed) return
      try {
        this.loading = true
        const response = await studentService.unbindPotentialWechat(this.detail.id)
        if (response.success) {
          this.detail = response.data
          await this.loadList()
          alert('解绑成功')
        }
      } catch (error) {
        console.error(error)
        const msg = error?.error || error?.message || '解绑失败，请重试'
        alert(msg)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.student-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.page-header .subtitle {
  color: #6b7280;
  margin-top: 0.25rem;
}
.filter-panel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  background: #fff;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}
.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.filter-item label {
  font-size: 0.85rem;
  color: #4b5563;
}
.filter-item input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.filter-actions {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
}
.primary-button {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #fff;
  padding: 0.55rem 1.2rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}
.ghost-button {
  background: #f3f4f6;
  color: #374151;
  padding: 0.55rem 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
.table-wrapper {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}
table {
  width: 100%;
  border-collapse: collapse;
}
thead th {
  background: #f9fafb;
  text-align: left;
  padding: 0.75rem 1rem;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}
tbody td {
  padding: 0.8rem 1rem;
  border-top: 1px solid #f1f5f9;
  vertical-align: top;
}
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
}
.empty-state {
  text-align: center;
  color: #6b7280;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}
.link-button {
  background: transparent;
  border: none;
  color: #2563eb;
  cursor: pointer;
  font-weight: 600;
}
.decrypt-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.15rem 0.3rem;
  margin-left: 0.25rem;
  color: #6b7280;
  vertical-align: middle;
  border-radius: 4px;
  transition: color 0.2s ease, background 0.2s ease;
}
.decrypt-btn:hover {
  color: #2563eb;
  background: rgba(37, 99, 235, 0.08);
}
.eye-icon {
  width: 16px;
  height: 16px;
  vertical-align: middle;
}
.wechat-info {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
}
.danger-button {
  background: #ef4444;
  color: #fff;
  padding: 0.5rem 0.9rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}
.danger-button:hover:not(:disabled) {
  background: #dc2626;
}
.danger-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.drawer-root {
  position: fixed;
  inset: 0;
  z-index: 80;
}
.drawer-scrim {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  cursor: pointer;
}
.drawer {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  width: 400px;
  max-width: 100%;
  height: 100%;
  background: #fff;
  box-shadow: -12px 0 32px rgba(15, 23, 42, 0.12);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
}
.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.drawer-body {
  overflow-y: auto;
  margin-top: 1rem;
}
.drawer-body section {
  background: #f9fafb;
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: left;
}
.close-button {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}
@media (max-width: 900px) {
  .drawer {
    width: 100%;
  }
}
</style>
